import { createSlice } from '@reduxjs/toolkit';

// for general setting that is not related to any view
const initialState = {
    openChatWidget: false,
    bearerToken: undefined,
    isVerifyEmailOptionVisible: true,
    verifyEmailTimer: null,
    // UI state
    sidebarCollapse: false,
    // Feature announcements already shown, keyed by company so each company sees each one
    // once. Survives logout along with the rest of `general`.
    seenAnnouncements: {},
    hostfullyGuidePreview: false,
    warningPopups: {
        SOC: true,
        GWC: true,
    },
    // Consent choices the admin opted to remember, keyed by lowercased login
    // email. Persisted via redux-persist so a returning admin sees pre-checked
    // boxes; survives logout because `general` is not reset by RESET_ALL_REDUCERS.
    rememberedConsent: {},
};

export const generalSlice = createSlice({
    name: 'general',
    initialState,
    reducers: {
        setOpenChatWidget: (state, action) => {
            state.openChatWidget = action.payload;
        },
        storeBearerToken: (state, action) => {
            state.bearerToken = action.payload;
        },
        clearBearerToken: (state) => {
            state.bearerToken = undefined;
        },
        submitVerifyEmailTimer: (state, action) => {
            state.verifyEmailTimer = action.payload;
            state.isVerifyEmailOptionVisible = true;
        },
        hideVerifyEmailOption: (state) => {
            state.isVerifyEmailOptionVisible = false;
        },
        clearVerifyEmailOptionState: (state) => {
            state.verifyEmailTimer = null;
            state.isVerifyEmailOptionVisible = true;
        },
        // UI state actions
        setSidebarCollapse: (state, action) => {
            state.sidebarCollapse = action.payload;
        },
        markAnnouncementSeen: (state, action) => {
            const { companyKey, id } = action.payload;
            // Older persisted state may be missing this key or hold a plain list.
            const seen =
                state.seenAnnouncements && !Array.isArray(state.seenAnnouncements)
                    ? state.seenAnnouncements
                    : {};
            state.seenAnnouncements = {
                ...seen,
                [companyKey]: [...(seen[companyKey] ?? []), id],
            };
        },
        showHostfullyGuidePreview: (state) => {
            state.hostfullyGuidePreview = true;
        },
        setWarningPopup: (state, action) => {
            state.warningPopups = {
                ...state.warningPopups,
                [action.payload.type]: action.payload.show,
            };
        },
        saveRememberedConsent: (state, action) => {
            const { email, choices } = action.payload;
            state.rememberedConsent = {
                ...state.rememberedConsent,
                [email]: choices,
            };
        },
        clearRememberedConsent: (state, action) => {
            const next = { ...state.rememberedConsent };
            delete next[action.payload.email];
            state.rememberedConsent = next;
        },
    },
});

export const {
    setOpenChatWidget,
    storeBearerToken,
    clearBearerToken,
    submitVerifyEmailTimer,
    hideVerifyEmailOption,
    clearVerifyEmailOptionState,
    setSidebarCollapse,
    markAnnouncementSeen,
    showHostfullyGuidePreview,
    setWarningPopup,
    saveRememberedConsent,
    clearRememberedConsent,
} = generalSlice.actions;

export default generalSlice.reducer;
